import { track } from '@sloyd/analytics';
import { actions, selectors } from '@sloyd/store-base';
import '@sloyd/types';
import { prompt, toast } from '@sloyd/ui';
import { bridge } from '@sloyd/viewer';
import { isEmpty } from 'lodash';
import { call, delay, fork, put, select, takeEvery } from 'saga-ts';
import { $a, guid4, invokeEvent, l, ROOT_OUTPUT } from 'shared-base';
import { randomize as randomizeParams } from '../utils/randomize';
import { loadEnvironmentFromModel } from './helpers/environment';
import { dataGuard, unityGuard } from './helpers/guards';
import { getIdAndName } from './helpers/idAndName';
import { addToAnotherProject } from './helpers/models';
import { navToCurrentProject, navToModel, navToNewModel } from './helpers/navigation';

export const saga = {
  id: 'sloyd.models',
  type: 'entity',
  root: root,
  trigger: {
    actionTypes: ['MODELS'],
  },
};

type Verb =
  | 'new' //
  | 'create' //
  | 'load'
  | 'pick'
  | 'rename'
  | 'delete'
  | 'share'
  | 'jsonDropped'
  | 'randomizeFrame'
  | 'randomizeObject'
  | 'randomizeMaterial'
  | 'randomizeBoth'
  | 'randomize'
  | 'duplicate'
  | 'drillUp';

type Action = {
  type: 'MODELS';
  verb: Verb;
  id: string;
  payload: Json;
};

const map: Record<Verb, any> = {
  new: newModel,
  create: createModel,
  load: loadModel,
  rename: renameModel,
  delete: deleteModel,
  duplicate: duplicateModel,
  share: share,
  drillUp: drillUp,
  pick: pickModel,
  jsonDropped: jsonDropped,
  randomize: randomize,
  randomizeFrame: randomizeFrame,
  randomizeObject: randomizeModel,
  randomizeMaterial: randomizeMaterial,
  randomizeBoth: randomizeBoth,
};

const analyitcsMapModels: any = {
  new: 'New Model',
  create: 'Create Model',
  load: 'Load Model',
  delete: 'Delete Model',
  rename: 'Rename Model',
  duplicate: 'Duplicate Model',
  share: 'Share Model',
  drillUp: 'Drill Up',
  pick: 'Pick Model',
  jsonDropped: 'Json Dropped',
  randomizeObject: 'Randomize Object',
  randomizeMaterial: 'Randomize Material',
  randomizeBoth: 'Randomize Both',
  randomizeFrame: 'Randomize Frame',
};

export function* jsonDropped(action: Action, model: IModel) {
  try {
    const { payload } = action;
    const { json } = payload;

    const modelId = guid4();
    delete json.id;

    const projectId = yield* select(selectors.single.$currentProjectId);

    yield put(
      $a('MODELS', 'create', modelId, {
        ...json,
        projectId,
      })
    );

    toast.show('Model imported as a new model');
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* share(action: Action, model: IModel, silent?: boolean) {}

export function* randomize(action: Action, model: IModel, silent?: boolean) {
  const { payload } = action;
  const { target } = payload;

  try {
    switch (target) {
      case 'shape':
        yield* randomizeModel(action, model, silent);
        break;
      case 'material':
        yield* randomizeMaterial(action, model, silent);
        break;
      case 'both':
        yield* randomizeBoth(action, model);
        break;
      default:
        yield* randomizeModel(action, model, silent);
        break;
    }
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* randomizeModel(action: Action, model: IModel, silent?: boolean) {
  try {
    const model = yield* select(selectors.single.$currentModel);
    if (!model) return;
    const parameters = yield* select(selectors.parameters.$parameters);
    const sliders = randomizeParams(parameters);

    if (!silent) {
      yield put(actions.valuesModel.patch(sliders));
    }

    bridge.patchValuesModel('main', sliders);

    setTimeout(() => {
      bridge.cameraFocus('main');
    }, 100);

    return sliders;
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* randomizeMaterial(action: Action, model: IModel, silent?: boolean) {
  try {
    const model = yield* select(selectors.single.$currentModel);
    if (!model) return;
    const generator = yield* select(selectors.single.$currentGenerator);
    if (!generator || !generator?.slots?.active) return;

    const pack = yield* select(selectors.single.$randomMaterialPack, Math.random());

    bridge.patchMaterials('main', pack);

    if (!silent) {
      yield put(actions.models.patch(model.id, { materials: pack }));
    }

    return pack;
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* randomizeBoth(action: Action, model: IModel) {
  try {
    const model = yield* select(selectors.single.$currentModel);
    if (!model) return;
    const pack = yield* randomizeMaterial(action, model, true);
    const sliders = yield* randomizeModel(action, model, true);
    yield put(actions.models.patch(model.id, { materials: pack, valuesModel: sliders }));
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* randomizeFrame(action: Action, model: IModel) {
  const { payload } = action;
  const { shiftKey } = payload;

  try {
    const editMode = yield* select(selectors.base.$editMode);

    if (shiftKey) {
      yield* randomizeBoth(action, model);
      return;
    }

    if (editMode === 'material') {
      yield* randomizeMaterial(action, model);
    } else {
      yield* randomizeModel(action, model);
    }
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* renameModel(action: Action, model: IModel) {
  try {
    if (!model) return;
    const { id, name } = model;

    const { value, didCancel } = yield prompt.input({
      title: 'Rename model',
      defaultValue: name,
      ctaButtonText: 'Rename',
    });

    if (didCancel) {
      return;
    }

    yield put(actions.models.patch(id, { name: value }));
    yield delay(50);

    toast.show(`Model successfully renamed to "${value}"`);
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* deleteModel(action: Action, model: IModel) {
  try {
    if (!model) return;
    const { id, name } = model;

    const { didCancel } = yield prompt.confirm({
      title: 'Delete Model',
      description: `Are you sure you want to delete "${name}"?`,
      ctaButtonText: 'Delete',
    });

    if (didCancel) {
      return;
    }

    yield put(actions.models.delete(id));
    yield delay(50);

    toast.show(`Model "${name}" successfully deleted`, { flavour: 'info' });
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* duplicateModel(action: Action, model: IModel) {
  try {
    if (!model) return;

    const { name } = model;

    const clone = { ...model, id: guid4() };
    toast.show(`Model "${name}" successfully duplicated`);

    yield put($a('MODELS', 'create', '', clone));
    yield delay(50);
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* newModel(action: Action) {
  try {
    yield navToNewModel();
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* createModel(action: Action) {
  const guid = action.id || guid4();
  console.time('createModel ' + guid);

  try {
    const { payload } = action;
    let { name, paletteId, generatorId, spawnPack } = payload;
    let materials = {};

    if (!name) {
      throw new Error('No name');
    }

    const generator = yield* select(selectors.single.$generator, generatorId);
    const { projectId } = yield* select(selectors.raw.$rawCurrentIds);

    if (!generatorId) {
      throw new Error('No generatorId');
    }

    if (!generator) {
      throw new Error('No generator');
    }

    // TODO: remove when spawnPack is improved
    spawnPack = undefined;

    if (spawnPack) {
      const pack = yield* select(selectors.single.$spawnPack, spawnPack);
      materials = pack ?? {};
    } else if (paletteId) {
      const palette = yield* select(selectors.single.$palette, paletteId);
      materials = palette?.materials ?? {};
    }

    const { defaultValues } = generator;

    const model: any = {
      id: guid,
      generatorId: '',
      generatorUrl: `${ROOT_OUTPUT}/${generatorId}.template.json`,
      thumbnail: '',
      modifiedAt: new Date().toISOString(),
      paletteId: '',
      valuesModel: { ...defaultValues, lod: 0.5 },
      materials,
      ...payload,
    };

    if (projectId === model.projectId || !model.projectId) {
      // same project
      yield put(actions.models.add(model));
    } else {
      // different project
      yield addToAnotherProject(model);
    }

    const models = yield* select(selectors.base.$models);
    const count = models.length;
    track('Count Models', { count });
  } catch (err) {
    invokeEvent('sentry', { err });
  }
  console.timeEnd('createModel ' + guid);
}

export function* loadModel(action: Action) {
  console.time('loadModel');

  try {
    yield unityGuard();

    const model = yield* select(selectors.raw.$rawModel);

    if (!model || isEmpty(model)) {
      throw new Error('No model');
    }

    if (!model.generatorId) {
      throw new Error('No generatorId');
    }

    const generator = yield* select(selectors.single.$generator, model.generatorId);
    const slotId = generator?.slots?.primary ?? '0';

    yield put(actions.currentIds.patch({ generatorId: model.generatorId, slotId }));
    bridge.setSlotId(slotId || '0');

    yield put(actions.appState.patch({ editMode: 'object', aiExplanation: '' }));

    bridge.replaceModel('main', model);
    yield fork(loadEnvironmentFromModel, model);

    yield delay(100);

    const { valuesModel } = model;

    if (valuesModel) {
      bridge.patchValuesModel('main', valuesModel);
    }

    yield put({ type: 'GOLDEN', verb: 'bootstrap' });

    yield delay(100);
  } catch (err) {
    invokeEvent('sentry', { err });
  }
  console.timeEnd('loadModel');
}

export function* drillUp(action: Action) {
  yield navToCurrentProject();
}

export function* pickModel(action: Action) {
  const { id } = action;
  yield navToModel(id);
}

export function* models(action: any) {
  const { verb, id } = action;
  yield delay(120);

  const saga = (map as any)[verb];

  if (!saga) {
    return;
  }

  l(`saga|saga.models ${verb} on ${id}`);

  console.time('dataGuard');
  yield* dataGuard();
  console.timeEnd('dataGuard');

  const model = yield* select(selectors.single.$model, id);
  const idAndName = yield* call(getIdAndName, { modelId: id });
  track(analyitcsMapModels[verb], { origin, ...idAndName });

  yield* fork(saga, action, model);
}

export function* root() {
  yield takeEvery('MODELS', models);
}
